import { createResizeObserverWithRAF } from '@/utils/resizeObserverWithRAF';
import { useCallback, useRef, useState } from 'react';

import {
  PRIMARY_NAV_ITEM_ESTIMATE_PX,
  PRIMARY_NAV_MORE_TEST_ID,
  PRIMARY_NAV_TEST_IDS,
  type PrimaryNavRailId,
} from './primary-nav-rail.constants';
import { type PrimaryNavMeasurement } from './split-primary-nav-rail';

const EMPTY: PrimaryNavMeasurement = {
  hasMeasured: false,
  availableHeight: 0,
  heights: new Map(),
  moreHeight: PRIMARY_NAV_ITEM_ESTIMATE_PX,
};

const SIDEBAR_CONTAINER_SELECTOR = '[data-testid="sidebar-container"]';
const SIDEBAR_CONTENT_SELECTOR = '[data-sidebar="content"]';

function outerHeight(el: HTMLElement): number {
  if (el.offsetHeight === 0) return 0;
  const style = getComputedStyle(el);
  return (
    el.offsetHeight +
    (parseFloat(style.marginTop) || 0) +
    (parseFloat(style.marginBottom) || 0)
  );
}

export interface PrimaryNavMeasureResult {
  measurement: PrimaryNavMeasurement;
  /** Attach to any real DOM element inside the primary nav SidebarGroup. */
  containerRef: (el: HTMLElement | null) => void;
  measure: () => void;
}

/**
 * Mirrors `useFooterRailMeasure`: attaches to a DOM element inside the primary
 * nav, walks up to the sidebar container for total height, measures individual
 * item heights by data-testid. The available height for primary nav items is
 * the sidebar container height minus chrome (header, create button, footer).
 */
export function usePrimaryNavMeasure(): PrimaryNavMeasureResult {
  const [measurement, setMeasurement] = useState<PrimaryNavMeasurement>(EMPTY);
  const elRef = useRef<HTMLElement | null>(null);
  const obsRef = useRef<ReturnType<typeof createResizeObserverWithRAF> | null>(null);

  const measure = useCallback(() => {
    const el = elRef.current;
    if (!el) return;

    // Walk up to the sidebar container (same as footer rail does)
    const container = el.closest(SIDEBAR_CONTAINER_SELECTOR);
    if (!(container instanceof HTMLElement)) return;

    // Total sidebar height
    const availableHeight = container.clientHeight;
    if (availableHeight <= 0) return;

    // The SidebarContent box — primary nav lives inside it
    const contentBox = container.querySelector(SIDEBAR_CONTENT_SELECTOR);

    // Chrome = everything except the content box (header, create button, footer)
    let chromeHeight = 0;
    for (const child of Array.from(container.children)) {
      if (!(child instanceof HTMLElement)) continue;
      // Skip the content box ancestor
      if (contentBox instanceof HTMLElement && child.contains(contentBox)) continue;
      chromeHeight += outerHeight(child);
    }

    // Chrome taller than the sidebar leaves the fixed items no room at all.
    // That is a real measurement, and the one that must fold everything: the
    // footer only starts folding once the primary nav has fully collapsed.
    const primaryAvailableHeight = Math.max(0, availableHeight - chromeHeight);

    // Measure each fixed item by testId
    const heights = new Map<PrimaryNavRailId, number>();
    for (const [id, testId] of Object.entries(PRIMARY_NAV_TEST_IDS) as [
      PrimaryNavRailId,
      string,
    ][]) {
      const itemEl = container.querySelector(
        `[data-testid="${testId}"]`
      ) as HTMLElement | null;
      if (itemEl) {
        const h = outerHeight(itemEl);
        if (h > 0) heights.set(id, h);
      }
    }

    // More trigger height
    const moreEl = container.querySelector(
      `[data-testid="${PRIMARY_NAV_MORE_TEST_ID}"]`
    ) as HTMLElement | null;
    const moreHeight =
      moreEl && moreEl.offsetHeight > 0
        ? outerHeight(moreEl)
        : PRIMARY_NAV_ITEM_ESTIMATE_PX;

    setMeasurement((prev) => {
      if (
        prev.hasMeasured &&
        prev.availableHeight === primaryAvailableHeight &&
        prev.moreHeight === moreHeight
      )
        return prev;
      return {
        hasMeasured: true,
        availableHeight: primaryAvailableHeight,
        heights,
        moreHeight,
      };
    });
  }, []);

  const containerRef = useCallback(
    (el: HTMLElement | null) => {
      obsRef.current?.disconnect();
      obsRef.current = null;
      elRef.current = el;
      if (!el) return;

      // Observe the sidebar container so resize events trigger re-measure
      const container = el.closest(SIDEBAR_CONTAINER_SELECTOR);
      const obs = createResizeObserverWithRAF(measure);
      if (container instanceof HTMLElement) obs.observe(container);
      obs.observe(el);
      obsRef.current = obs;
      measure();
    },
    [measure]
  );

  return { measurement, containerRef, measure };
}
